// src/services/availabilityRules.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

/**
 * Availability Rules API (admin only, every route needs the admin JWT).
 *
 *   GET   /availability-rules/admin        every rule, archived included,
 *                                          each with publicMessageIsPlaceholder
 *   POST  /availability-rules              create
 *   PUT   /availability-rules/:id          update
 *   PATCH /availability-rules/:id/archive  soft delete
 *   PATCH /availability-rules/:id/restore  restore
 *
 * There is NO public rules route, on purpose: the public page only sees the
 * resolved status and one message through POST /events/check-availability.
 *
 * Based at the API ROOT (empty relative base) with the full relative path in
 * each query, the same shape as serviceCatalog.js.
 */
const availabilityRulesApi = createApi({
  reducerPath: "availabilityRules",
  baseQuery: prepareBaseQuery(""),
  tagTypes: ["AvailabilityRule"],
  endpoints: (builder) => ({
    // Admin: full list including archived ({ items: [...] })
    getAvailabilityRulesAdmin: builder.query({
      query: () => ({ url: "availability-rules/admin", method: "GET" }),
      providesTags: ["AvailabilityRule"],
    }),

    createAvailabilityRule: builder.mutation({
      query: (body) => ({
        url: "availability-rules",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AvailabilityRule"],
    }),

    updateAvailabilityRule: builder.mutation({
      query: ({ id, body }) => ({
        url: `availability-rules/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AvailabilityRule"],
    }),

    archiveAvailabilityRule: builder.mutation({
      query: (id) => ({
        url: `availability-rules/${id}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: ["AvailabilityRule"],
    }),

    restoreAvailabilityRule: builder.mutation({
      query: (id) => ({
        url: `availability-rules/${id}/restore`,
        method: "PATCH",
      }),
      invalidatesTags: ["AvailabilityRule"],
    }),
  }),
});

export const {
  useGetAvailabilityRulesAdminQuery,
  useCreateAvailabilityRuleMutation,
  useUpdateAvailabilityRuleMutation,
  useArchiveAvailabilityRuleMutation,
  useRestoreAvailabilityRuleMutation,
} = availabilityRulesApi;

export default availabilityRulesApi;
export { availabilityRulesApi };
